import { useMemo } from 'react';
import { Card, CardHeader, CardBody, Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui';
import censusData from '../../data/census/all-counties.json';
import zillowStates from '../../data/parsed/zillow-state-summary.json';
import mortgageRates from '../../data/economic/mortgage-rates.json';

export default function AnalyticsPage() {
  const counties = censusData as any[];
  const states = zillowStates as any[];
  const rates = mortgageRates as any[];

  const nationalStats = useMemo(() => {
    const totalValue = counties.reduce((sum, c) => sum + (c.medianValue || 0) * (c.housingUnits || 0), 0);
    const totalUnits = counties.reduce((sum, c) => sum + (c.housingUnits || 0), 0);
    const sorted = [...counties].sort((a, b) => a.medianValue - b.medianValue);
    return {
      avgValue: totalValue / totalUnits,
      totalUnits,
      medianValue: sorted[Math.floor(counties.length / 2)]?.medianValue || 0
    };
  }, [counties]);

  const stateStats = useMemo(() => {
    return [...states].sort((a, b) => b.latestValue - a.latestValue).map((s, i) => ({ ...s, rank: i + 1 }));
  }, [states]);

  const rateTrend = useMemo(() => rates.slice(-26).reverse(), [rates]);

  const formatCurrency = (v: number) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
  const formatNumber = (v: number) => new Intl.NumberFormat('en-US').format(v);

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Property Analytics</h1>
        <p className="text-gray-600 mt-1">
          Comprehensive analysis of {formatNumber(counties.length)} US counties and {formatNumber(states.length)} states
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="bg-blue-500 text-white">
          <CardBody>
            <p className="text-blue-100">National Average Value</p>
            <p className="text-3xl font-bold mt-2">{formatCurrency(nationalStats.avgValue)}</p>
            <p className="text-blue-200 text-sm">Weighted by housing units</p>
          </CardBody>
        </Card>
        <Card className="bg-green-500 text-white">
          <CardBody>
            <p className="text-green-100">Median County Value</p>
            <p className="text-3xl font-bold mt-2">{formatCurrency(nationalStats.medianValue)}</p>
            <p className="text-green-200 text-sm">Middle county</p>
          </CardBody>
        </Card>
        <Card className="bg-purple-500 text-white">
          <CardBody>
            <p className="text-purple-100">Total Housing Units</p>
            <p className="text-3xl font-bold mt-2">{formatNumber(nationalStats.totalUnits)}</p>
            <p className="text-purple-200 text-sm">Across all counties</p>
          </CardBody>
        </Card>
        <Card className="bg-orange-500 text-white">
          <CardBody>
            <p className="text-orange-100">Current Mortgage Rate</p>
            <p className="text-3xl font-bold mt-2">{rates[rates.length - 1]?.rate}%</p>
            <p className="text-orange-200 text-sm">30-year fixed (FRED)</p>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <h3 className="text-xl font-semibold">State Home Value Rankings (Zillow ZHVI)</h3>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">Rank</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Current ZHVI</TableHead>
                <TableHead className="text-right">2000 Value</TableHead>
                <TableHead className="text-right">Appreciation</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {stateStats.map((state) => (
                <TableRow key={state.state}>
                  <TableCell className="font-semibold">{state.rank}</TableCell>
                  <TableCell className="font-medium">{state.state}</TableCell>
                  <TableCell className="text-right text-green-600 font-semibold">{formatCurrency(state.latestValue)}</TableCell>
                  <TableCell className="text-right">{formatCurrency(state.oldestValue)}</TableCell>
                  <TableCell className="text-right text-green-600 font-semibold">+{state.changePercent?.toFixed(0)}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-xl font-semibold">Top 30 Most Expensive Counties</h3>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>County</TableHead>
                <TableHead>State</TableHead>
                <TableHead className="text-right">Median Value</TableHead>
                <TableHead className="text-right">Housing Units</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...counties].sort((a, b) => b.medianValue - a.medianValue).slice(0, 30).map((county, idx) => (
                <TableRow key={county.fips}>
                  <TableCell className="font-semibold">{idx + 1}</TableCell>
                  <TableCell className="font-medium">{county.name?.split(',')[0]}</TableCell>
                  <TableCell>{county.name?.split(',')[1]?.trim()}</TableCell>
                  <TableCell className="text-right text-green-600 font-semibold">{formatCurrency(county.medianValue)}</TableCell>
                  <TableCell className="text-right text-gray-500">{formatNumber(county.housingUnits)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h3 className="text-xl font-semibold">Mortgage Rate Trend (Last 26 Weeks)</h3>
        </CardHeader>
        <CardBody className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Rate</TableHead>
                <TableHead>Trend</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rateTrend.map((rate, i) => {
                const prev = rates[rates.length - 26 + i - 1];
                const change = prev ? rate.rate - prev.rate : 0;
                return (
                  <TableRow key={rate.date}>
                    <TableCell className="font-medium">{rate.date}</TableCell>
                    <TableCell className="text-right font-semibold">{rate.rate}%</TableCell>
                    <TableCell>{change > 0 ? '📈' : change < 0 ? '📉' : '➖'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardBody>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="bg-blue-50 border-blue-200">
          <CardBody>
            <h4 className="font-semibold text-blue-900">📊 Census Bureau</h4>
            <p className="text-blue-700 text-sm mt-2">{formatNumber(counties.length)} counties with property values, income, and housing data.</p>
          </CardBody>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardBody>
            <h4 className="font-semibold text-green-900">📈 Zillow Research</h4>
            <p className="text-green-700 text-sm mt-2">ZHVI time series for {formatNumber(states.length)} states from 2000 to present.</p>
          </CardBody>
        </Card>
        <Card className="bg-purple-50 border-purple-200">
          <CardBody>
            <h4 className="font-semibold text-purple-900">🏦 FRED</h4>
            <p className="text-purple-700 text-sm mt-2">{formatNumber(rates.length)} weekly 30-year mortgage rate records.</p>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
